"use client";

import type { LeadFlowAnswerStore } from "../types";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { getSessionKey, getSessionParameters } from "../utils/get-session-key";

type SessionUrlWatcherProps = {
  store: LeadFlowAnswerStore;
};

/**
 * Starts a fresh answer session whenever the page URL changes for any reason
 * other than a lead flow's own step parameter. The session's query parameters
 * go with it so the store can apply allowed query prefills. Render it inside
 * its own `Suspense` boundary so reading search params never opts the page
 * out of prerendering.
 */
function SessionUrlWatcher({ store }: SessionUrlWatcherProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sessionParameters = useMemo(
    () => getSessionParameters(searchParams ?? new URLSearchParams()),
    [searchParams],
  );
  const sessionKey =
    pathname === null ? null : getSessionKey(pathname, sessionParameters);

  useEffect(() => {
    if (sessionKey !== null) {
      store.resetFor(sessionKey, sessionParameters);
    }
  }, [sessionKey, sessionParameters, store]);

  return null;
}

export { SessionUrlWatcher };
